import { FC, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { useBreakpoint, useRelativeTime } from '@factorialco/deprecated-design-system'
import { PersonAvatarVariant } from '@factorialco/f0-react'
import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import {
  Calendar,
  Clock,
  Download,
  EyeVisible,
  Pencil,
  SolidPlay,
  SolidStop,
} from '@factorialco/f0-react/icons/app'
import { ProcessTargetDataTableDocument } from 'generated/resources/datatable.graphql'
import {
  ExportPerformanceReviewProcessDocument,
  PerformanceReviewOverviewHeaderDocument,
  PerformanceReviewOverviewHeaderEstimatedTargetFragment,
  PerformanceReviewOverviewHeaderFragment,
  PerformanceReviewOverviewHeaderLaunchedDocument,
  PerformanceReviewOverviewHeaderTargetFragment,
  PerformanceReviewOverviewParticipationRateTaskDocument,
  PerformanceReviewOverviewPeerAssignmentTaskDocument,
  PerformanceReviewOverviewPendingAgreementsTaskDocument,
  PerformanceReviewOverviewPendingEvaluationsTaskDocument,
} from 'generated/resources/graphql'
import { compact } from 'lodash'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useSubscription } from 'modules/core/hooks/useSubscription'
import buildUrl from 'modules/core/lib/buildUrl'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useLayoutContext } from 'modules/layout/hooks/useFullscreen'
import { reviewTypePicker } from 'modules/performance_process/components/common/filters/reviewTypePicker'
import { QuestionnairePreview } from 'modules/performance_process/components/questionnaire/QuestionnairePreview'
import { sectionSearchParam } from 'modules/performance_process/components/review/EditionWindow/editionWindowOptions'
import { LaunchModal } from 'modules/performance_process/components/review/launch/LaunchModal'
import { ReopenReviewModal } from 'modules/performance_process/components/review/reOpen/reOpenReviewModal'
import { RemoveScheduleReviewModal } from 'modules/performance_process/components/review/removeSchedule/removeScheduleReviewModal'
import { SaveAsTemplateModal } from 'modules/performance_process/components/review/saveAsTemplate/saveAsTemplateModal'
import { validationChecker } from 'modules/performance_process/components/review/show/helpers/validationChecker'
import { StopReviewModal } from 'modules/performance_process/components/review/stop/stopReviewModal'

import { getStatusMapping } from './tagConstants'

type HeaderProps = React.ComponentProps<typeof ResourceHeader>

interface Props {
  reviewProcess: PerformanceReviewOverviewHeaderFragment
  firstTargets: PerformanceReviewOverviewHeaderTargetFragment[]
  firstEstimatedTargets: PerformanceReviewOverviewHeaderEstimatedTargetFragment[]
  targetsCount: number
  estimatedTargetsCount: number
  hasCyclesFeatureEnabled: boolean
  isJobCatalogTreeEnabled: boolean
}

export const ReviewOverviewHeader: FC<Props> = ({
  reviewProcess,
  firstTargets,
  firstEstimatedTargets,
  targetsCount,
  estimatedTargetsCount,
  hasCyclesFeatureEnabled,
  isJobCatalogTreeEnabled,
}) => {
  const { isEmbedded } = useLayoutContext()
  const { isMobile } = useBreakpoint()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { employee } = useCurrentSession()
  const apolloClient = useApolloClient()

  const [showLaunchModal, setShowLaunchModal] = useState(false)
  const [showStopReviewModal, setShowStopReviewModal] = useState(false)
  const [showReopenReviewModal, setShowReopenReviewModal] = useState(false)
  const [showRemoveScheduleReviewModal, setShowRemoveScheduleReviewModal] = useState(false)
  const [showSaveAsTemplateModal, setShowSaveAsTemplateModal] = useState(false)
  const [previewSidepanelOpen, setPreviewSidepanelOpen] = useState(false)

  const [exportReviewProcess, { loading: isExporting }] = useMutation(
    ExportPerformanceReviewProcessDocument,
    {
      extractorFn: (data) => data.performanceMutations.exportPerformanceReviewProcess,
    }
  )

  const isDraft = reviewProcess.status === 'draft'
  const isScheduled = reviewProcess.status === 'scheduled'
  const isStarting = reviewProcess.status === 'starting'
  const isActive = reviewProcess.status === 'active'

  const isMissingReviewerStrategy = validationChecker(
    reviewProcess.startValidationErrors ?? [],
    'missing_reviewer_strategy'
  )

  const canManage = !!reviewProcess._policies?.manage.can
  const canExport = !!reviewProcess._policies?.export.can

  const reviewDeadline = reviewProcess.endsAt
  const { formatted: formattedDeadline } = useRelativeTime({ date: reviewDeadline })

  const [statusLabel, statusVariant] = getStatusMapping(reviewProcess.status)

  const useEstimatedTargets = isDraft || isScheduled || isStarting
  const reviewTargets = useEstimatedTargets ? firstEstimatedTargets : firstTargets
  const reviewTargetsCount = useEstimatedTargets ? estimatedTargetsCount : targetsCount

  const onExport = async () => {
    const result = await exportReviewProcess({
      variables: {
        id: reviewProcess.id,
      },
    })

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('performance_process.reviews.export.error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: employee
          ? i18n.t('performance_process.reviews.export.success_my_documents')
          : i18n.t('performance_process.reviews.export.success_documents'),
      })
    }

    return handled
  }

  const getPrimaryAction = (): HeaderProps['primaryAction'] => {
    if (!canManage) return undefined

    switch (reviewProcess.status) {
      case 'scheduled':
        return {
          label: i18n.t('performance_process.reviews.unschedule_review'),
          onClick: () => setShowRemoveScheduleReviewModal(true),
        }

      case 'active':
        return {
          label: i18n.t('performance_process.reviews.stop_review'),
          onClick: () => setShowStopReviewModal(true),
          icon: SolidStop,
        }

      case 'draft':
        return {
          label: i18n.t('performance_process.reviews.launch_review_button'),
          onClick: () => {
            setShowLaunchModal(true)
          },
          icon: SolidPlay,
        }

      case 'finished':
        return {
          label: i18n.t('performance_process.reviews.reopen_review_button'),
          onClick: () => setShowReopenReviewModal(true),
          icon: SolidPlay,
        }

      default:
        return undefined
    }
  }

  const getSecondaryActions = (): HeaderProps['secondaryActions'] => {
    if (isMobile || (!canManage && !canExport)) return []

    const actions = []

    if (canExport) {
      actions.push({
        label: i18n.t('performance_process.reviews.export.title'),
        icon: Download,
        disabled: isExporting,
        hideLabel: true,
        onClick: onExport,
      })
    }

    if (canManage) {
      actions.push({
        label: i18n.t('performance_process.reviews.go_to_edit_review_button'),
        icon: Pencil,
        onClick: () =>
          navigate(
            navigation.performanceProcess.reviews.review.settings.fullPath({
              reviewId: reviewProcess.id,
            })
          ),
      })

      actions.push({
        label: i18n.t('performance_process.reviews.templates.save_as_template.action'),
        onClick: () => setShowSaveAsTemplateModal(true),
      })
    }

    if (isDraft && !isMissingReviewerStrategy) {
      actions.push({
        label: i18n.t('performance_process.reviews.preview'),
        icon: EyeVisible,
        onClick: () => setPreviewSidepanelOpen(true),
      })
    }

    return actions
  }

  // Subscribe to the review process started event and refetch the header query
  // to update the UI when the review process changes from starting to active
  useSubscription(PerformanceReviewOverviewHeaderLaunchedDocument, {
    skip: reviewProcess.status !== 'starting',
    onData: async ({ data: subscriptionData }) => {
      const subscriptionReviewProcess =
        subscriptionData?.data?.performanceReviewProcessStart?.reviewProcess

      if (subscriptionReviewProcess?.id === reviewProcess.id) {
        await apolloClient.refetchQueries({
          include: [
            PerformanceReviewOverviewHeaderDocument,
            PerformanceReviewOverviewParticipationRateTaskDocument,
            PerformanceReviewOverviewPendingEvaluationsTaskDocument,
            PerformanceReviewOverviewPendingAgreementsTaskDocument,
            PerformanceReviewOverviewPeerAssignmentTaskDocument,
            ProcessTargetDataTableDocument,
          ],
        })
        setShowLaunchModal(false)
      }
    },
  })

  // Hide the header when embedded in mobile WebView
  if (isEmbedded) return null

  return (
    <>
      <ResourceHeader
        title={reviewProcess.name || i18n.t('performance_process.reviews.untitled')}
        description={
          reviewProcess.description || i18n.t('performance_process.reviews.no_description')
        }
        status={
          statusLabel
            ? {
                label: '',
                text: statusLabel,
                variant: statusVariant,
              }
            : undefined
        }
        primaryAction={getPrimaryAction()}
        secondaryActions={getSecondaryActions()}
        metadata={compact(
          // Cycle tag
          [
            hasCyclesFeatureEnabled && {
              label: i18n.t('performance_cycles.cycle_filter.label'),
              value: {
                type: 'text',
                content:
                  reviewProcess.performanceCycle?.title ??
                  (isDraft ? i18n.t('performance_process.reviews.select_cycle') : '-'),
              },
              icon: Clock,
              actions:
                canManage && isDraft
                  ? [
                      {
                        label: i18n.t('common.edit'),
                        icon: Pencil,
                        onClick: () =>
                          navigate(
                            navigation.performanceProcess.reviews.review.settings.fullPath({
                              reviewId: reviewProcess.id,
                            })
                          ),
                      },
                    ]
                  : undefined,
            },
            // Due date
            {
              label: i18n.t('performance_process.reviews.common.due_date'),
              value: {
                type: 'text',
                content:
                  formattedDeadline ||
                  i18n.t('performance_process.reviews.launch_deadline_missing'),
              },
              icon: Calendar,
              actions:
                canManage && (isActive || isDraft)
                  ? [
                      {
                        label: i18n.t('performance_process.reviews.launch_deadline_missing'),
                        icon: Pencil,
                        onClick: () =>
                          navigate(
                            navigation.performanceProcess.reviews.review.settings.fullPath({
                              reviewId: reviewProcess.id,
                            }) + sectionSearchParam('timeline')
                          ),
                      },
                    ]
                  : undefined,
            },
            // Review type
            canManage && {
              label: i18n.t('performance_process.reviews.launch_review_types'),
              value: {
                type: 'text',
                content:
                  reviewProcess.reviewerStrategies
                    ?.map((type) => reviewTypePicker(type))
                    .join(' • ') ||
                  i18n.t('performance_process.reviews.missing_review_type_header'),
              },
              actions: isDraft
                ? [
                    {
                      label: i18n.t('performance_process.reviews.missing_review_type_header'),
                      icon: Pencil,
                      onClick: () =>
                        navigate(
                          navigation.performanceProcess.reviews.review.settings.fullPath({
                            reviewId: reviewProcess.id,
                          }) + sectionSearchParam('review_types')
                        ),
                    },
                  ]
                : undefined,
            },
            // Review targets
            canManage && {
              label: i18n.t('performance_process.reviews.audience.title'),
              value: !reviewTargetsCount
                ? {
                    type: 'text',
                    content: isDraft
                      ? i18n.t('performance_process.reviews.missing_target_header')
                      : '-',
                  }
                : reviewTargetsCount > 10
                  ? {
                      type: 'text',
                      content: i18n.t('performance_process.reviews.audience.participants_count', {
                        count: reviewTargetsCount,
                      }),
                    }
                  : {
                      type: 'list',
                      variant: 'person',
                      avatars: reviewTargets.map(
                        (target): PersonAvatarVariant => ({
                          firstName: target.access?.firstName ?? '',
                          lastName: target.access?.lastName ?? '',
                          type: 'person',
                          src: buildUrl(target.access?.avatar),
                        })
                      ),
                    },
              actions: isDraft
                ? [
                    {
                      label: i18n.t('common.edit'),
                      icon: Pencil,
                      onClick: () =>
                        navigate(
                          navigation.performanceProcess.reviews.review.settings.fullPath({
                            reviewId: reviewProcess.id,
                          }) + sectionSearchParam('target_group')
                        ),
                    },
                  ]
                : undefined,
            },
          ]
        )}
      />

      {showLaunchModal && (
        <LaunchModal review={reviewProcess} onClose={() => setShowLaunchModal(false)} />
      )}
      {showStopReviewModal && (
        <StopReviewModal
          reviewId={reviewProcess.id}
          onClose={() => setShowStopReviewModal(false)}
        />
      )}
      {showReopenReviewModal && (
        <ReopenReviewModal
          reviewId={reviewProcess.id}
          onClose={() => setShowReopenReviewModal(false)}
        />
      )}
      {showRemoveScheduleReviewModal && (
        <RemoveScheduleReviewModal
          reviewId={reviewProcess.id}
          name={reviewProcess.name}
          onClose={() => setShowRemoveScheduleReviewModal(false)}
        />
      )}
      {showSaveAsTemplateModal && (
        <SaveAsTemplateModal
          reviewId={reviewProcess.id}
          reviewName={reviewProcess.name}
          onClose={() => setShowSaveAsTemplateModal(false)}
        />
      )}

      <Sidepanel
        open={previewSidepanelOpen}
        onClose={() => setPreviewSidepanelOpen(false)}
        width="wideXL"
        title={i18n.t('performance_process.reviews.preview_title')}
      >
        <QuestionnairePreview
          reviewType={reviewProcess.reviewerStrategies ?? []}
          questionnaires={reviewProcess.questionnaires ?? undefined}
          isCompetenciesAssessmentsEnabled={
            reviewProcess.competenciesAssessmentsConfiguration?.enabled ?? false
          }
          scoreConfiguration={reviewProcess.scoreConfiguration}
          scoreScale={reviewProcess.scoreScale.scale}
          isJobCatalogTreeEnabled={isJobCatalogTreeEnabled}
        />
      </Sidepanel>
    </>
  )
}
